import {SelectionModel} from '@angular/cdk/collections';
import {AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, Renderer2, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {DisplayedColumnInf} from '../../../interfaces/common/table/displayed-column-inf';
import {MatTable, MatTableDataSource} from '@angular/material/table';
import * as _ from 'lodash';
import {PageEventCursorInf} from './page-event-cursor';
import {CursorPaginatorComponent} from '../../../components/cursor-paginator/cursor-paginator.component';
import {Sort} from '@angular/material/sort';


@Directive()
export class Table implements AfterViewInit, OnInit {

  selection = new SelectionModel<any>(true, []);
  dataSource;

  @ViewChild(MatPaginator) tablePaginator: MatPaginator;
  @ViewChild(CursorPaginatorComponent) cursorPaginator;

  @Input() pageSize = 25;
  @Input() length;
  @Input() pageSizeOptions = [25, 50, 100];

  @Input() isClientSide = false;
  @Input() pageEventCursor: PageEventCursorInf;

  @Output() onSelection = new EventEmitter<any>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() pageChangedCursor = new EventEmitter<PageEventCursorInf>();
  @Output() sortChange = new EventEmitter<Sort>();

  @ViewChild(MatTable, {read: ElementRef} ) private matTableRef: ElementRef;
  @ViewChild('tableMainContainer') tableMainContainer: ElementRef;

  @Input() isPagedTable = true;
  @Input() isCursorPageTable = false;
  @Input() isSelectedByDefault = true;

  _displayedColumns: Array<DisplayedColumnInf>;
  lastDisplayColumnsLength: number;
  pressed = false;
  currentResizeIndex: number;
  startX: number;
  startWidth: number;
  isResizingRight: boolean;
  resizableMousemove: () => void;
  resizableMouseup: () => void;
  tableFrameSize: number;
  refreshCount = 3;

  constructor(private renderer: Renderer2) {
    this.dataSource = new MatTableDataSource<any>([]);
  }

  ngOnInit(): void {
    this.lastDisplayColumnsLength = this.getDisplayColumnsLength();

    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const matched = [];
      this._displayedColumns.forEach(column => {
        const columnData = data[column.name] !== null ? '' + data[column.name] : '';
        if (columnData.toLowerCase().includes(filter)) {
          matched.push(true);
        }
      });
      return matched.length > 0;
    };
  }

  ngAfterViewInit(): void {
    if (!_.isNil(this.tablePaginator)) {
      this.tablePaginator.page.subscribe(pageInfo => {
        if (this.pageSize !== pageInfo.pageSize) {
          this.pageSize = pageInfo.pageSize;
          this.firstPage();
        }

        this.pageChanged.emit(pageInfo);
      });
    }

    const observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      if (width !== 0) {
        this.setTableResize(width);
      }

    });

    observer.observe(this.tableMainContainer.nativeElement);
    this.setTableResize(this.tableMainContainer.nativeElement.clientWidth);
  }

  @Input()
  get displayedColumns(): Array<DisplayedColumnInf> { return this._displayedColumns; }
  set displayedColumns(allColumns: Array<DisplayedColumnInf>) {
    this._displayedColumns = allColumns;
    this._displayedColumns.forEach((column) => {
      column.originalWidth = column.width;
    });
  }

  @Input() set isMultipleSelection(isMultipleSelection) {
    this.selection = new SelectionModel<any>(isMultipleSelection,
      this.selection.isEmpty() ? [] : [this.isSelectedByDefault ? this.selection.selected[0] : []]);
    if (this.isSelectedByDefault) {
      this.onSelection.emit(isMultipleSelection ? this.selection.selected : this.selection.selected[0]);
    }
  }

  @Input() set filter(value) {
    this.dataSource.filter = _.isString(value) ? value.trim().toLowerCase() : null;

    if (this.dataSource.filteredData && this.dataSource.filteredData.length > 0){
      this.selection.select(this.selection.isMultipleSelection() ? this.dataSource.filteredData : this.dataSource.filteredData[0]);
      if (this.isSelectedByDefault) {
        this.selectionChanged(this.selection);
      }
    }else{
      this.selection.clear();
      if (this.isSelectedByDefault) {
        this.onSelection.emit(this.selection.isMultipleSelection() ? [] : null);
      }
    }
  }

  @Input() set data(data) {
    if (_.isNil(data)) {
      return;
    }

    this.dataSource.data = data;

    // This is due to fact that we initialize the dataSource every time
    // for server side we want to keep the paginator as it was first initialized
    if (this.isClientSide){
      this.dataSource.paginator = this.tablePaginator;
    }

    if (!_.isNil(data) && data.length > 0) {
      const lastSelection = (_.isNil(this.selection) || this.selection.isEmpty())
        ? [data[0]] : this.findSelectedItems(data);
      this.selection.clear();
      if (this.isSelectedByDefault) {
        this.selection.select(...lastSelection);
        this.onSelection.emit(this.selection.isMultipleSelection() ? this.selection.selected : this.selection.selected[0]);
      }
    }else{
      this.selection.clear();
      if (this.isSelectedByDefault) {
        this.onSelection.emit(this.selection.isMultipleSelection() ? [] : null);
      }
    }
  }

  @Input() set search(searchValue) {
    this.dataSource.filter = searchValue;
  }

  selectionChanged(selection): void {
    this.onSelection.emit(this.selection.isMultipleSelection() ? selection.selected : selection.selected[0]);
  }

  onItemClicked(row: DisplayedColumnInf, event): void {
    if (event && event.shiftKey && this.selection.isMultipleSelection() && this.selection.hasValue()) {
      window.getSelection().removeAllRanges();

      const all: any[] = this.selection.selected;
      const lastSelected = all[all.length - 1];
      let fromIndex = this.dataSource.data.findIndex((item) => item.id === lastSelected.id);
      let toIndex = this.dataSource.data.findIndex((item) => item.id === row.id);

      if (toIndex < fromIndex) {
        [toIndex, fromIndex] = [fromIndex, toIndex];
      }
      const selectItems = this.dataSource.data.slice(fromIndex, toIndex + 1);
      this.selection.clear();
      selectItems.forEach((item) => this.selection.select(item));

    } else if (event && event.ctrlKey && this.selection.isMultipleSelection()) {
      this.selection.toggle(row);
    } else {
      this.selection.clear();
      this.selection.toggle(row);
    }
  }

  getDisplayedColumns(): Array<string> {
    const currentColumns = this._displayedColumns.filter(cd => cd.selected).map(cd => cd.name);
    if (this.lastDisplayColumnsLength !== currentColumns.length) {


      this.refreshCount -= 1;
      if (this.refreshCount === 0){
        this.setTableResize(this.tableMainContainer.nativeElement.clientWidth);
        this.refreshCount = 5;
        this.lastDisplayColumnsLength = currentColumns.length;
      }

    }
    return this._displayedColumns.filter(cd => cd.selected).map(cd => cd.name);
  }

  isAllSelected(): any {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;

    return numSelected === numRows;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  checkboxLabel(row?): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  getColumnName(columnId): any {
    return this._displayedColumns.find(column => column.name === columnId).display;
  }

  private findSelectedItems(items): any {
    const currentSelectedItems = [];

    this.selection.selected.forEach((selectedItem) => {
      const currentSelectedItem = items.find(item => item.id === selectedItem.id);

      if (!_.isNil(currentSelectedItem)) {
        currentSelectedItems.push(currentSelectedItem);
      }
    });

    if (currentSelectedItems.length === 0) {
      return [items[0]];
    }

    return currentSelectedItems;
  }


  // Columns resize
  setTableResize(tableWidth: number): any {
    let totWidth = 0;
    this._displayedColumns.forEach(( column) => {
      if (column.selected) {
        totWidth += column.width;
      }
    });


    let scale = -1;
    // in case table container width is larger than what the total of columns are asking
    if ((tableWidth >= totWidth)) {
      scale = ((tableWidth - 5) / totWidth);

    } else {
      scale = 1;
      this._displayedColumns.forEach(( column) => {

        if (column.selected) {
          column.width = (column.width / totWidth) * this.getOrigTableWidth();
          this.setColumnWidth(column);
        }
      });


    }




    this._displayedColumns.forEach(( column) => {

      if (column.selected) {
        column.width *= scale;
        this.setColumnWidth(column);
      }
    });
    this.tableFrameSize = tableWidth;
  }

  onResizeColumn(event: any): void {

    const index = event.currentTarget.cellIndex;
    this.checkResizing(event, index);
    this.currentResizeIndex = index;
    this.pressed = true;
    this.startX = event.pageX;
    this.startWidth = event.target.clientWidth;
    event.preventDefault();
    this.mouseMove(index);
  }

  private checkResizing(event, index): void {
    const cellData = this.getCellData(index);
    this.isResizingRight = (index === 0) ||
      (Math.abs(event.pageX - cellData.right) < cellData.width / 2 && index !== this.getDisplayColumnsLength() - 1);
  }

  private getCellData(index: number): any {
    const headerRow = this.matTableRef.nativeElement.children[0];
    const cell = headerRow.children[0].children[index];
    return cell.getBoundingClientRect();
  }

  mouseMove(index: number): void {
    this.resizableMousemove = this.renderer.listen('document', 'mousemove', (event) => {
      if (this.pressed && event.buttons ) {
        const dx = (this.isResizingRight) ? (event.pageX - this.startX) : (-event.pageX + this.startX);
        const width = this.startWidth + dx;
        if ( this.currentResizeIndex === index && width > 50 ) {
          this.setColumnWidthChanges(index, width);
        }
      }
    });
    this.resizableMouseup = this.renderer.listen('document', 'mouseup', (event) => {
      if (this.pressed) {
        this.pressed = false;
        this.currentResizeIndex = -1;
        this.resizableMousemove();
        this.resizableMouseup();
      }
    });
  }

  setColumnWidthChanges(index: number, width: number): void {
    const orgWidth = this.getColumnFromDisplayColumn(index).width;
    const dx = width - orgWidth;
    if ( dx !== 0 ) {
      const j = ( this.isResizingRight ) ? index + 1 : index - 1;
      const newWidth = this.getColumnFromDisplayColumn(j).width - dx;
      if ( newWidth > 50 ) {
        this.getColumnFromDisplayColumn(index).width = width;
        this.setColumnWidth(this.getColumnFromDisplayColumn(index));
        this.getColumnFromDisplayColumn(j).width = newWidth;
        this.setColumnWidth(this.getColumnFromDisplayColumn(j));
      }
    }
  }

  setColumnWidth(column: any): void {
    const columnEls = Array.from( document.getElementsByClassName('mat-column-' + column.name.replaceAll(' ', '-')) );
    columnEls.forEach(( el: HTMLDivElement ) => {
      el.style.width = column.width + 'px';
    });
  }

  onResize(event): void {
    this.setTableResize(this.tableMainContainer.nativeElement.clientWidth);
  }

  getDisplayColumnsLength(): any {
    return this._displayedColumns.filter((column) => column.selected).length;
  }

  getColumnFromDisplayColumn(index): any {
    return this._displayedColumns.filter((column) => column.selected)[index];
  }

  // Cursor
  cursorPageChanged(event: PageEventCursorInf): void {
    this.pageChangedCursor.emit(event);
  }

  getOrigTableWidth(): number {
    let origWidth = 0;
    this._displayedColumns.filter((column) => column.selected).forEach((column1) => {
      origWidth += column1.originalWidth;
    });
    return origWidth;
  }

  firstPage(): void {
    if (this.isCursorPageTable) {
      this.cursorPaginator.defaultCursor();
    }

    if (this.isPagedTable) {
      this.tablePaginator.pageIndex = 0;
    }
  }

  dataDirection(event: Sort): void{
    this.sortChange.emit(event);
  }

  matSortActive(): string{
    const sortColumn =  this.displayedColumns.find(column => column.sort === true);
    if (sortColumn){
      return sortColumn.name;
    } else {
      return '';
    }
  }
}
